/**
 * Careers page - Comprehensive redesign
 */

import React, {useState} from 'react';
import {Link} from 'react-router-dom';
import {LandingLayout} from '../../components/layout/LandingLayout';
import {Container} from '../../components/ui/Container';
import {Section} from '../../components/ui/Section';
import {SectionHeader} from '../../components/ui/SectionHeader';
import {Card} from '../../components/ui/Card';
import {Button} from '../../components/ui/Button';
import {ROUTES} from '../../config/constants';
import {useScrollAnimation} from '../../hooks/useScrollAnimation';

// Job positions
interface JobPosition {
  title: string;
  department: string;
  location: string;
  type: string;
  description: string;
  requirements?: string[];
}

const openPositions: JobPosition[] = [
  {
    title: 'Senior Full-Stack Engineer',
    department: 'Engineering',
    location: 'Remote',
    type: 'Full-time',
    description: 'Build the next generation of meeting intelligence tools. Work with cutting-edge AI technologies and help shape the future of how teams collaborate.',
    requirements: ['5+ years full-stack experience', 'TypeScript, React, Node.js', 'Experience with AI/ML integrations'],
  },
  {
    title: 'Product Designer',
    department: 'Design',
    location: 'Remote',
    type: 'Full-time',
    description: 'Design beautiful, intuitive experiences for our users. Help create products that make meetings more productive and enjoyable.',
    requirements: ['3+ years product design experience', 'Strong portfolio', 'Experience with B2B SaaS'],
  },
  {
    title: 'Customer Success Manager',
    department: 'Customer Success',
    location: 'Remote',
    type: 'Full-time',
    description: 'Help our customers succeed with VoxaPilot. Build relationships, drive adoption, and ensure customer satisfaction.',
    requirements: ['2+ years customer success experience', 'Strong communication skills', 'SaaS experience preferred'],
  },
  {
    title: 'AI/ML Engineer',
    department: 'Engineering',
    location: 'Remote',
    type: 'Full-time',
    description: 'Push the boundaries of AI-powered meeting intelligence. Work on transcription, summarization, and insight extraction.',
    requirements: ['3+ years ML/AI experience', 'Python, TensorFlow/PyTorch', 'NLP experience preferred'],
  },
  {
    title: 'Sales Engineer',
    department: 'Sales',
    location: 'Remote',
    type: 'Full-time',
    description: 'Bridge the gap between our technical product and enterprise customers. Help prospects understand how VoxaPilot can transform their workflows.',
    requirements: ['Technical background', 'Sales or pre-sales experience', 'Strong presentation skills'],
  },
  {
    title: 'DevOps Engineer',
    department: 'Engineering',
    location: 'Remote',
    type: 'Full-time',
    description: 'Build and maintain our infrastructure. Ensure reliability, scalability, and security of our platform.',
    requirements: ['3+ years DevOps experience', 'AWS/GCP, Kubernetes', 'CI/CD pipelines'],
  },
];

// Benefits - removed emojis
const benefits = [
  {
    title: 'Competitive Compensation',
    description: 'We offer competitive salary packages with equity participation, ensuring you share in our success as we grow.',
  },
  {
    title: 'Health & Wellness',
    description: 'Comprehensive health, dental, and vision coverage for you and your family, plus mental health support and wellness programs.',
  },
  {
    title: 'Unlimited PTO',
    description: 'Take time off when you need it. We trust you to manage your time and prioritize work-life balance.',
  },
  {
    title: 'Remote First',
    description: 'Work from anywhere in the world. We\'re a distributed team that values flexibility and autonomy.',
  },
  {
    title: 'Learning & Development',
    description: 'Annual budget for courses, books, conferences, and certifications. We invest in your growth and career development.',
  },
  {
    title: 'Top-Tier Equipment',
    description: 'We provide the best tools and equipment you need to do your best work, including a generous home office setup budget.',
  },
  {
    title: '401(k) Matching',
    description: 'Plan for your future with our 401(k) matching program and financial planning resources.',
  },
  {
    title: 'Team Events',
    description: 'Regular team offsites, virtual events, and opportunities to connect with colleagues from around the world.',
  },
];

// Culture values
const cultureValues = [
  {
    title: 'Ownership',
    description: 'We trust you to own your work and make decisions. Take initiative, experiment, and drive impact.',
  },
  {
    title: 'Growth Mindset',
    description: 'We\'re always learning and improving. Embrace challenges, seek feedback, and continuously develop your skills.',
  },
  {
    title: 'Collaboration',
    description: 'We work together to solve hard problems. Share knowledge, support teammates, and build something great together.',
  },
  {
    title: 'Customer Focus',
    description: 'Everything we do starts with understanding our customers. Build products that solve real problems and create value.',
  },
];

// Departments
const departments = ['All', 'Engineering', 'Design', 'Sales', 'Customer Success', 'Marketing', 'Operations'];

export function CareersPage() {
  const [selectedDepartment, setSelectedDepartment] = useState('All');
  const {ref: heroRef, isVisible: heroVisible} = useScrollAnimation();
  const {ref: cultureRef, isVisible: cultureVisible} = useScrollAnimation();
  const {ref: benefitsRef, isVisible: benefitsVisible} = useScrollAnimation();
  const {ref: jobsRef, isVisible: jobsVisible} = useScrollAnimation();
  const {ref: ctaRef, isVisible: ctaVisible} = useScrollAnimation();

  const filteredPositions =
    selectedDepartment === 'All'
      ? openPositions
      : openPositions.filter((position) => position.department === selectedDepartment);

  return (
    <LandingLayout>
      {/* Hero Section */}
      <Section padding="lg" background="white" className="pt-32 pb-20 md:pb-24 bg-background dark:bg-[#1E1E1E]">
        <Container>
          <div
            ref={heroRef as React.RefObject<HTMLDivElement>}
            className={`max-w-4xl mx-auto text-center transition-all duration-700 ${
              heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-textPrimary dark:text-[#CCCCCC] mb-6 leading-tight">
              Join Our Team
            </h1>
            <p className="text-xl sm:text-2xl text-textSecondary dark:text-[#858585] max-w-3xl mx-auto leading-relaxed mb-8">
              Help us build the future of meeting intelligence. We're looking for passionate, talented people who want to make an impact.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#open-positions">
                <Button variant="primary" size="lg" className="bg-[#1E1E1E] dark:bg-[#1E1E1E] hover:bg-[#2D2D30] dark:hover:bg-[#2D2D30] text-white">
                  View Open Positions
                </Button>
              </a>
              <Link to={ROUTES.CONTACT}>
                <Button variant="outline" size="lg">
                  Get in Touch
                </Button>
              </Link>
            </div>
          </div>
        </Container>
      </Section>

      {/* Culture & Values */}
      <Section padding="lg" background="gray" ref={cultureRef as React.RefObject<HTMLElement>} className="bg-backgroundSecondary dark:bg-[#252526]">
        <Container>
          <div className={`transition-all duration-700 ${
            cultureVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <SectionHeader
              title="Our Culture"
              subtitle="What it's like to work at VoxaPilot"
              centered
            />
            <div className={`max-w-4xl mx-auto mb-12 transition-all duration-700 ${
              cultureVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            style={{transitionDelay: '0.1s'}}
            >
              <div className="prose prose-lg max-w-none text-textSecondary dark:text-[#858585] leading-relaxed space-y-4 text-center">
                <p>
                  We're building a team of passionate, talented people who want to make meetings better. If you're excited about AI, productivity, and creating amazing user experiences, we'd love to hear from you.
                </p>
                <p>
                  At VoxaPilot, we believe in ownership, growth, and collaboration. We're a remote-first company that values flexibility, autonomy, and work-life balance. We're building something meaningful, and we want you to be part of it.
                </p>
              </div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
              {cultureValues.map((value, index) => (
                <div
                  key={value.title}
                  className={`transition-all duration-700 ${
                    cultureVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                  style={{transitionDelay: `${0.2 + (index * 0.1)}s`}}
                >
                  <Card hover className="p-6 h-full">
                    <div className="space-y-4">
                      <h3 className="text-xl font-semibold text-textPrimary dark:text-[#CCCCCC]">
                        {value.title}
                      </h3>
                      <p className="text-base text-textSecondary dark:text-[#858585] leading-relaxed">
                        {value.description}
                      </p>
                    </div>
                  </Card>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </Section>

      {/* Benefits */}
      <Section padding="lg" background="white" ref={benefitsRef as React.RefObject<HTMLElement>} className="bg-background dark:bg-[#1E1E1E]">
        <Container>
          <div className={`transition-all duration-700 ${
            benefitsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <SectionHeader
              title="Benefits & Perks"
              subtitle="We take care of our team"
              centered
            />
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
              {benefits.map((benefit, index) => (
                <div
                  key={benefit.title}
                  className={`transition-all duration-700 ${
                    benefitsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                  style={{transitionDelay: `${0.2 + (index * 0.1)}s`}}
                >
                  <Card hover className="p-6 h-full">
                    <div className="space-y-3">
                      <h3 className="font-semibold text-lg text-textPrimary dark:text-[#CCCCCC]">
                        {benefit.title}
                      </h3>
                      <p className="text-sm text-textSecondary dark:text-[#858585] leading-relaxed">
                        {benefit.description}
                      </p>
                    </div>
                  </Card>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </Section>

      {/* Open Positions */}
      <Section padding="lg" background="gray" ref={jobsRef as React.RefObject<HTMLElement>} id="open-positions" className="bg-backgroundSecondary dark:bg-[#252526]">
        <Container>
          <div className={`transition-all duration-700 ${
            jobsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <SectionHeader
              title="Open Positions"
              subtitle="Join us in building the future of meeting intelligence"
              centered
            />

            {/* Department Filters */}
            <div className="flex flex-wrap justify-center gap-2 mb-12">
              {departments.map((dept) => (
                <button
                  key={dept}
                  onClick={() => setSelectedDepartment(dept)}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    selectedDepartment === dept
                      ? 'bg-[#1E1E1E] dark:bg-gray-700 text-white'
                      : 'bg-white dark:bg-[#252526] text-textSecondary dark:text-[#858585] hover:bg-gray-50 dark:hover:bg-[#2D2D30] border border-gray-200 dark:border-gray-700'
                  }`}
                >
                  {dept}
                </button>
              ))}
            </div>

            {/* Job Listings */}
            {filteredPositions.length > 0 ? (
                      <div className="space-y-4 max-w-4xl mx-auto">
                        {filteredPositions.map((position, index) => (
                            <div
                              key={position.title}
                              className={`transition-all duration-700 ${
                                jobsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                              }`}
                              style={{transitionDelay: `${0.2 + (index * 0.1)}s`}}
                            >
                      <Card hover className="p-6 md:p-8">
                        <div className="flex flex-col gap-6">
                          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                            <div className="flex-1">
                              <h3 className="font-semibold text-xl text-textPrimary dark:text-[#CCCCCC] mb-3">
                                {position.title}
                              </h3>
                              <div className="flex flex-wrap gap-3 text-sm text-textSecondary dark:text-[#858585] mb-3">
                                <span className="px-3 py-1 bg-gray-100 dark:bg-[#2D2D30] rounded-md">
                                  {position.department}
                                </span>
                                <span className="px-3 py-1 bg-gray-100 dark:bg-[#2D2D30] rounded-md">
                                  {position.location}
                                </span>
                                <span className="px-3 py-1 bg-gray-100 dark:bg-[#2D2D30] rounded-md">
                                  {position.type}
                                </span>
                              </div>
                              <p className="text-base text-textSecondary dark:text-[#858585] leading-relaxed mb-4">
                                {position.description}
                              </p>
                              {position.requirements && position.requirements.length > 0 && (
                                <div className="mt-4">
                                  <p className="text-sm font-medium text-textPrimary dark:text-[#CCCCCC] mb-2">
                                    Key Requirements:
                                  </p>
                                  <ul className="space-y-1">
                                    {position.requirements.map((req, i) => (
                                      <li key={i} className="text-sm text-textSecondary dark:text-[#858585] flex items-start">
                                        <span className="text-orange-600 dark:text-orange-500 mr-2">•</span>
                                        {req}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                            <div className="flex-shrink-0">
                              <Button
                                variant="primary"
                                size="md"
                                className="bg-[#1E1E1E] dark:bg-[#1E1E1E] hover:bg-[#2D2D30] dark:hover:bg-[#2D2D30] text-white w-full md:w-auto"
                                onClick={() => {
                                  // TODO: Link to application form or external job board
                                  window.open('#', '_blank');
                                }}
                              >
                                Apply Now
                              </Button>
                            </div>
                          </div>
                        </div>
                      </Card>
                    </div>
                        ))}
                      </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-lg text-textSecondary dark:text-[#858585]">
                  No positions available in this department at the moment. Check back soon or{' '}
                  <Link to={ROUTES.CONTACT} className="text-orange-600 dark:text-orange-500 hover:underline">
                    get in touch
                  </Link>{' '}
                  to learn about future opportunities.
                </p>
              </div>
            )}
          </div>
        </Container>
      </Section>

      {/* CTA Section */}
      <Section padding="lg" background="white" ref={ctaRef as React.RefObject<HTMLElement>} className="bg-background dark:bg-[#1E1E1E]">
        <Container>
          <div className={`max-w-3xl mx-auto text-center transition-all duration-700 ${
            ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <h2 className="text-3xl sm:text-4xl font-bold text-textPrimary dark:text-[#CCCCCC] mb-4">
              Don't See a Role That Fits?
            </h2>
            <p className="text-lg text-textSecondary dark:text-[#858585] mb-8 max-w-2xl mx-auto leading-relaxed">
              We're always looking for talented people who share our passion for building great products. Send us your resume and we'll keep you in mind for future opportunities.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to={ROUTES.CONTACT}>
                <Button variant="primary" size="lg" className="bg-[#1E1E1E] dark:bg-[#1E1E1E] hover:bg-[#2D2D30] dark:hover:bg-[#2D2D30] text-white">
                  Get in Touch
                </Button>
              </Link>
              <a href="mailto:careers@voxapilot.com">
                <Button variant="outline" size="lg">
                  Email Us
                </Button>
              </a>
            </div>
            <p className="text-sm text-textTertiary dark:text-[#6A6A6A] mt-6">
              Send your resume to <a href="mailto:careers@voxapilot.com" className="text-orange-600 dark:text-orange-500 hover:underline">careers@voxapilot.com</a>
            </p>
          </div>
        </Container>
      </Section>
    </LandingLayout>
  );
}
